// === Widget Configuration (from data attributes + API) ===

export interface WidgetConfig {
  apiKey: string;
  apiUrl: string;
  position: WidgetPosition;
  theme: WidgetTheme;
  greeting: string;
  placeholder: string;
  requireContactInfo: boolean;
  contactFields: ContactField[];
  /** Auto-reply shown after an offline submission. Server-authoritative (widget settings); the local value is only a fallback. */
  offlineMessage: string;
  /** What to do when nobody is online: lead-capture form or hide the widget. */
  offlineMode: OfflineMode;
  /** Intro copy above the offline form (client-side; `data-offline-intro`). */
  offlineIntroText: string;
  /** Header status line while offline. */
  offlineStatusText: string;
  brandName: string;
  brandLogoUrl?: string;
  statusText: string;
  poweredByVisible: boolean;
}

export type WidgetPosition = 'bottom-right' | 'bottom-left';

/** Mirrors backend `OfflineModes` (snake_case wire values). */
export type OfflineMode = 'lead_capture' | 'hide_widget';

export function isOfflineMode(value: unknown): value is OfflineMode {
  return value === 'lead_capture' || value === 'hide_widget';
}

export interface WidgetTheme {
  primaryColor: string;
  textColor: string;
  backgroundColor: string;
  headerTextColor: string;
  borderRadius: number;
  fontFamily: string;
}

export type ContactField = 'name' | 'email' | 'phone';

// === API Response Types (matching backend DTOs) ===

export interface ConversationResponse {
  id: string;
  subscriberId: string;
  productId: string;
  status: ConversationStatus;
  subject: string;
  visitor: VisitorInfo;
  channel: string;
  createdAt: string;
  lastMessageAt: string;
  /** True when the server found nobody online at creation and inserted the offline auto-reply. */
  capturedOffline?: boolean;
  /** The persisted System auto-reply (only on `POST /api/widget/conversations` when `capturedOffline`). */
  autoReply?: MessageResponse | null;
}

export type ConversationStatus = 'New' | 'Open' | 'Resolved' | 'Closed';

export interface VisitorInfo {
  name?: string;
  email?: string;
  phone?: string;
  externalId: string;
}

export interface MessageResponse {
  id: string;
  conversationId: string;
  content: string;
  contentType: 'Text' | 'Html';
  senderType: 'Visitor' | 'Agent' | 'System';
  senderName?: string;
  createdAt: string;
}

export interface PaginatedMessages {
  data: MessageResponse[];
  total: number;
  page: number;
  limit: number;
}

export interface SessionResponse {
  token: string;
  visitorId: string;
  expiresAt: string;
}

/** Shape of `GET /api/widget/config` (Envy.Messaging `WidgetConfigResponse`). */
export interface WidgetConfigResponse {
  enabled: boolean;
  greeting: string | null;
  offlineMessage: string;
  offlineMode: OfflineMode | string;
  /** Presence snapshot at load time — the widget keeps it fresh via `GET /api/widget/presence`. */
  isOnline: boolean;
  collectEmail: boolean;
  collectPhone: boolean;
  collectName: boolean;
  requireEmailBeforeChat: boolean;
  theme: {
    primaryColor: string;
    /** Backend enum serialised as a string. */
    position: 'BottomRight' | 'BottomLeft' | string;
    bubbleIcon: string | null;
    headerText: string | null;
  } | null;
}

/** Shape of `GET /api/widget/presence`. */
export interface WidgetPresenceResponse {
  isOnline: boolean;
  offlineMode: OfflineMode | string;
}

// === SignalR Hub Event Types (matching Envy.Messaging.Hubs.IWidgetClient) ===

export interface WidgetMessageEvent {
  id: string;
  conversationId: string;
  senderType: 'Visitor' | 'Agent' | 'System';
  senderName?: string;
  content: string;
  contentType: string;
  createdAt: string;
}

// === Internal State ===

export interface SmsConsentRequest {
  phone: string;
  consentText: string;
  visitorName?: string;
  visitorEmail?: string;
}

export interface WidgetState {
  isOpen: boolean;
  isLoading: boolean;
  hasSession: boolean;
  hasContactInfo: boolean;
  smsOptedIn: boolean;
  conversations: ConversationResponse[];
  activeConversation: ConversationResponse | null;
  messages: MessageResponse[];
  unreadCount: number;
  error: string | null;
  visitorInfo: Partial<VisitorInfo>;
  remoteConfig: WidgetConfigResponse | null;
  /** null until the first config/presence response arrives. */
  isOnline: boolean | null;
}

export const DEFAULT_WIDGET_CONFIG: Omit<WidgetConfig, 'apiKey' | 'apiUrl'> = {
  position: 'bottom-right',
  theme: {
    primaryColor: '#6366f1',
    textColor: '#1f2937',
    backgroundColor: '#ffffff',
    headerTextColor: '#ffffff',
    borderRadius: 16,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  greeting: 'Hi there! How can we help you today?',
  placeholder: 'Type your message...',
  requireContactInfo: true,
  contactFields: ['name', 'email'],
  offlineMessage: "Thanks! We'll get back to you shortly.",
  offlineMode: 'lead_capture',
  offlineIntroText: "No one is available right now. Leave your details and a message and we'll get back to you.",
  offlineStatusText: "We're away — leave a message",
  brandName: '',
  statusText: 'We typically reply within a few minutes',
  poweredByVisible: true,
};
